import { useQuery } from '@tanstack/react-query';
import { getAccountAuth, getAuthCurrent, getPipelinesWorkspaceAuth, getJourneysWorkspaceAuth } from './api';
import type { AugmentedAccount, AugmentedUser } from '../../stores/auth';
import { setAccountStore, setUserStore } from '../../stores/auth';
import { getMasqId } from './../../utils/masq';
import type { Account, User } from './';
import { Region } from './';
import { AppBundle, AppBundleRoutes } from '../../utils/app-bundles';
import { analytics } from '../../utils/analytics';
import { getWorkspaceIdFromUrl } from '../../context/current-workspace';
import { setActiveAccount } from '@customerio/hydra-nav-state';
import { setServicesApiRegion } from '@/services/api/deprecated';
import type { AxiosError } from 'axios';

/**
 * Get auth token from cookies
 */

const MAX_RETRY_COUNT = 6;
const RETRYABLE_ERRORS = ['ERR_NETWORK', 'ERR_NETWORK_CHANGED'];

export type Auth = {
  account: AugmentedAccount | null;
  user: AugmentedUser | null;
};

function getAuthMethod(): () => Promise<{ account: Account; user: User }> {
  const currentUrl = window.location.href;
  const workspaceId = getWorkspaceIdFromUrl(currentUrl);

  const masqAccountId = getMasqId(currentUrl);
  if (masqAccountId != null && !isNaN(parseInt(masqAccountId))) {
    return async () => {
      return getAccountAuth(parseInt(masqAccountId));
    };
  }

  if (workspaceId != null && !isNaN(parseInt(workspaceId))) {
    if (currentUrl.includes(`/workspaces/${workspaceId}${AppBundleRoutes[AppBundle.Pipelines]}`)) {
      return async () => {
        return getPipelinesWorkspaceAuth(parseInt(workspaceId));
      };
    }

    return async () => {
      return getJourneysWorkspaceAuth(parseInt(workspaceId));
    };
  }

  return getAuthCurrent;
}

function isAxiosError(error: any): error is AxiosError {
  return error?.isAxiosError;
}

function isRetryableError(error: any): boolean {
  return isAxiosError(error) && RETRYABLE_ERRORS.includes(error.code ?? '');
}

export function useAuth() {
  /**
   * Check if we are authenticated and get the access token
   */
  return useQuery({
    retry: (failureCount, error) => {
      // Retry on network errors
      if (isRetryableError(error) && failureCount < MAX_RETRY_COUNT) {
        return true;
      }
      return false;
    },
    queryKey: ['authenticated'],
    queryFn: async () => {
      try {
        const auth: Auth = { account: null, user: null };
        const query = getAuthMethod();
        const result = await query();

        if (result.account != null) {
          if (result.account.is_sample_account_access) {
            const accountResult = await getAccountAuth(result.user.account_id);
            auth.account = setAccountStore(accountResult.account);
          } else {
            auth.account = setAccountStore(result.account);
          }
        }

        if (result.user != null) {
          auth.user = setUserStore(result.user);
        }

        assignAuthInfoGlobal(auth);
        updateActiveAccount(auth);

        return auth;
      } catch (error) {
        if (isRetryableError(error)) {
          // Allow retrying on network errors--throwing the error will trigger a retry attempt
          throw error;
        }
        return null;
      }
    },
  });
}

function updateActiveAccount(auth: Auth) {
  const regionId = auth.account?.region;
  let regionName: string | undefined;
  if (regionId != null) {
    regionName = regionId === Region.EU ? 'eu' : 'us';
  }
  setActiveAccount({
    id: auth.account?.id,
    regionId,
    regionName,
  });

  setServicesApiRegion(regionId ?? Region.US);
}

// Exposes auth info for external scripts like analytics trackers.
function assignAuthInfoGlobal(auth: Auth) {
  Object.assign(window, {
    __hydra__: {
      account: {
        id: auth.account?.id,
        dc: auth.account?.region === Region.EU ? 'eu' : 'us',
      },
      user: {
        id: auth.user?.id,
        email: auth.user?.email,
      },
      analytics,
    } satisfies Window['__hydra__'],
  });
}
